import { createFileRoute, Link } from "@tanstack/react-router";
import { ServicePageLayout } from "@/components/service/ServicePageLayout";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";

const SLUG = "/commercial-roof-coating";
const NAME = "Commercial Roof Coating";
const TITLE = "Commercial Roof Coating Phoenix, AZ | Silicone & Elastomeric | Durafoam";
const DESCRIPTION =
  "Phoenix commercial roof coating and recoating — elastomeric and silicone systems over foam, mod-bit, and BUR. Extends roof life 10–15 years at 20–30% of replacement cost.";

const faqs = [
  {
    q: "What is a commercial roof coating?",
    a: "A commercial roof coating is a liquid-applied elastomeric or silicone membrane sprayed over an existing roof. It restores waterproofing, boosts reflectivity to cool-roof levels, and typically adds 10–15 years of service life at 20–30% of the cost of full replacement. It works on foam, modified bitumen, BUR, and sometimes single-ply substrates in good structural shape.",
  },
  {
    q: "Elastomeric vs. silicone — which coating is better in Phoenix?",
    a: "Both work. Elastomeric (acrylic) coatings cost less, breathe better, and are the standard for foam recoats. Silicone coatings shed water even better and hold up under ponding, so they're often preferred on flat commercial roofs where drainage isn't perfect. We spec based on your specific roof — see our silicone vs. elastomeric guide for details.",
  },
  {
    q: "How much does a commercial roof coating cost in Phoenix?",
    a: "Commercial recoats typically run $2.50–$4.50 per square foot depending on coating type, mils applied, and prep required. That's roughly 20–30% of the cost of a full replacement, which is why recoating is the highest-ROI decision most commercial owners can make on an aging-but-sound roof.",
  },
  {
    q: "How long does a commercial roof coating last?",
    a: "A properly applied elastomeric or silicone system in Phoenix typically holds about 5 years in the Phoenix sun before the next recoat. On a foam roof, this is the recurring cycle that makes SPF a genuinely renewable system — recoat on schedule and the foam substrate underneath keeps going indefinitely.",
  },
  {
    q: "Can you coat over TPO or PVC single-ply?",
    a: "Sometimes, with the right primer and coating chemistry. Adhesion to weathered single-ply is chemistry-specific and manufacturer-dependent. We inspect first and won't quote a coating job we don't believe will bond — usually if the TPO is that far gone, converting to foam is the better call.",
  },
  {
    q: "Can you recoat a ponding roof?",
    a: "Silicone can handle ponding water where acrylic elastomerics can't. If the roof has chronic ponding, silicone is often the right chemistry — and if the ponding is severe enough, we may recommend spraying additional foam in the low spots first to actually re-slope the roof toward drains before we coat.",
  },
  {
    q: "Do you qualify commercial recoats for utility cool-roof rebates?",
    a: "Yes — most bright-white elastomeric and silicone systems easily meet the SRI thresholds APS, SRP, and Phoenix-metro utility cool-roof rebate programs require. We document the coating spec at proposal time so you can submit the paperwork.",
  },
  {
    q: "How disruptive is a commercial coating job?",
    a: "Much less than a full replacement. No tear-off, no dumpsters, no exposed deck. Spray phases can produce solvent odors that need to be scheduled around tenants, but most commercial buildings stay fully operational throughout.",
  },
];

export const Route = createFileRoute("/commercial-roof-coating")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) =>
    serviceHead({ override: loaderData?.seo,
      slug: SLUG,
      title: TITLE,
      description: DESCRIPTION,
      serviceName: NAME,
      faqs,
      rating: loaderData?.rating,
      reviewCount: loaderData?.reviewCount,
    }),
  component: Page,
});

function Page() {
  return (
    <ServicePageLayout
      pageSlug="commercial-roof-coating"
      eyebrow="Commercial"
      h1="Commercial Roof Coating in Phoenix, AZ"
      subhead="Elastomeric and silicone recoats for Phoenix commercial roofs — restore waterproofing, hit cool-roof SRI, and add 10–15 years at 20–30% of replacement cost. Coating over foam, modified bitumen, BUR, and select single-ply substrates."
      heroBullets={[
        "Elastomeric & silicone systems, spec'd to your roof",
        "Cool-roof rebate documentation at proposal",
        "5-year recoat cycle vs. 15–20 year replacement",
        "Coating over foam, mod-bit, BUR & select single-ply",
      ]}
      whatIs={{
        heading: "What is commercial roof coating?",
        body: (
          <>
            <p>
              A commercial roof coating is a liquid-applied elastomeric or silicone membrane
              sprayed over your existing roof. Instead of tearing off a weathered but structurally
              sound roof and starting over, we prep the existing surface, prime it, and spray a
              new monolithic waterproof layer directly over it. The finished roof is bright white,
              seamless, and reflective — ready for another decade-plus of service.
            </p>
            <p>
              This is the highest-ROI move most commercial owners can make on an aging roof.
              Replacement costs 3–4× what a coating does. And on a foam roof, the recoat cycle is
              what makes the whole system genuinely renewable — recoat every 5 years and the
              foam substrate underneath keeps going indefinitely.
            </p>
            <h3 className="mt-8 text-xl font-bold text-primary">What coatings actually do</h3>
            <ul>
              <li>
                <strong>Restore waterproofing.</strong> A fresh coating seals hairline surface
                cracks, re-terminates around every penetration, and returns the roof to a
                monolithic waterproof surface.
              </li>
              <li>
                <strong>Boost reflectivity.</strong> Bright-white elastomeric and silicone
                coatings hit SRI values that qualify for most Phoenix-metro utility cool-roof
                rebate programs and measurably reduce HVAC load.
              </li>
              <li>
                <strong>Add real service life.</strong> A properly applied coating typically adds
                about 5 years before the next recoat is due.
              </li>
              <li>
                <strong>Preserve the substrate.</strong> On foam roofs specifically, timely
                recoating is what keeps the foam substrate from ever needing replacement.
              </li>
            </ul>
          </>
        ),
      }}
      climate={{
        heading: "Coating chemistry for Phoenix commercial roofs",
        body: (
          <>
            <p>
              Not every coating works on every roof. Chemistry matters — especially in Phoenix,
              where UV, thermal cycling, and monsoon downpours punish anything that isn't spec'd
              right. Here's how we pick:
            </p>
            <ul>
              <li>
                <strong>Elastomeric (acrylic).</strong> Standard for foam recoats. Breathes well,
                bonds excellently to foam, and delivers strong reflectivity at reasonable cost.
                Our default on well-drained SPF roofs.
              </li>
              <li>
                <strong>Silicone.</strong> Best in class for ponding-water resistance and long-term
                UV stability. Doesn't breathe like acrylic, so it's less suited for uncoated
                foam — but excellent as a topcoat on mod-bit, BUR, or foam roofs with chronic
                ponding.
              </li>
              <li>
                <strong>Polyurea and hybrid systems.</strong> For high-traffic or
                mechanically-loaded commercial roofs (rooftop restaurants, service platforms), we
                spec a tougher polyurea topcoat.
              </li>
            </ul>
            <p>
              For a deeper dive into the trade-offs, see our{" "}
              <Link
                to="/silicone-vs-elastomeric-roof-coatings-for-foam-roofs-in-phoenix"
                className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
              >
                silicone vs. elastomeric guide
              </Link>
              .
            </p>
          </>
        ),
      }}
      process={{
        heading: "Our commercial coating process",
        steps: [
          {
            title: "Roof survey & moisture check",
            body: "We walk the roof, inspect every penetration and seam, and pull moisture readings where warranted. If the substrate is wet or structurally compromised, we say so — coating over a bad roof is the fastest way to waste money. We only recommend a recoat when the underlying roof is genuinely sound.",
          },
          {
            title: "Written scope with coating spec",
            body: "Coating chemistry, mils applied, manufacturer, warranty terms, and cool-roof rebate documentation. When more than one chemistry honestly fits your roof, we quote them side by side.",
          },
          {
            title: "Prep, prime, and spray",
            body: "Clean, remove all dust and chalking, address any repair items (flashings, drain rebuilds, foam infill for ponding areas), prime as spec'd, and spray the coating in monitored lifts to full mil thickness. Wet-mil checks throughout.",
          },
          {
            title: "Warranty & recoat scheduling",
            body: "Manufacturer material warranty plus our workmanship warranty. We schedule your next recoat inspection at year 8–10 so the cycle stays on track — this is what keeps foam roofs going indefinitely.",
          },
        ],
      }}
      whyChoose={{
        heading: "Coating vs. replacement — when each makes sense",
        body: (
          <>
            <ul>
              <li>
                <strong>Coat when:</strong> The substrate is intact, the roof has real service
                life left underneath, and the failure mode is coating end-of-life (reflectivity
                gone, hairline surface cracks, chalking). This is the case on almost every
                properly maintained foam roof, and on many mod-bit and BUR roofs at the 12–15
                year mark.
              </li>
              <li>
                <strong>Replace when:</strong> The substrate itself is saturated with trapped
                moisture, structurally compromised, or has been patched so many times there's no
                honest coating job to be had. When this is the call, foam is almost always our
                reroof recommendation — see{" "}
                <Link
                  to="/commercial-foam-roofing"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  commercial foam roofing
                </Link>
                .
              </li>
              <li>
                <strong>Repair-then-coat when:</strong> The roof needs targeted fixes (flashings,
                drains, ponding fill) before a coating will hold. This is common; we quote the
                repair scope inside the coating proposal so you see the real total.
              </li>
            </ul>
            <p>
              For repair-only work, see{" "}
              <Link
                to="/commercial-roof-repair"
                className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
              >
                commercial roof repair
              </Link>
              . For real Phoenix numbers, see{" "}
              <Link
                to="/commercial-roofing-cost-phoenix"
                className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
              >
                commercial roofing cost
              </Link>
              .
            </p>
          </>
        ),
      }}
      faqs={faqs}
      ctaTitle="Recoat now — or replace in 5 years for 4× the cost?"
      ctaBody="Call us or request a free commercial inspection. We'll walk the roof, pull moisture readings where warranted, and hand you a written recommendation — recoat, targeted repair, or (honestly) replace. Whichever the roof actually needs."
    />
  );
}
